import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { Collapse } from '@material-ui/core'
import RightPanel from '../../../RightPanel'
import SydTabs from '../../../../commonDesign/SydTabs'
import Icon from '../../../../atoms/Icon'
import Markup from '../../../../atoms/Markup'
import FlexRow from '../../../../molecules/FlexRow'
import { getWorkflowStatus, labelStyle as detailLabelStyle, valueStyle as detailValueStyle } from '../shared/redtailUIHelpers'
import { LinkedContactRow, LinkedContactDetail } from '../shared/LinkedContactDisplay'

const FILTER_ALL = 'all'
const FILTER_OUTSTANDING = 'outstanding'
const FILTER_OVERDUE = 'overdue'

function getCurrentStepName (workflow) {
  if (!workflow.current_step_id || !workflow.workflow_steps?.length) return null
  const step = workflow.workflow_steps.find(s => s.id === workflow.current_step_id)
  return step?.name || null
}

const statusTabOptions = {
  [FILTER_ALL]: 'All',
  [FILTER_OUTSTANDING]: 'Outstanding',
  [FILTER_OVERDUE]: 'Overdue'
}

function WorkflowDetail ({ workflow }) {
  const currentStep = getCurrentStepName(workflow)
  const dueFormatted = workflow.due_date
    ? dayjs(workflow.due_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{ padding: '8px 1.5rem 4px 28px' }}>
      <LinkedContactDetail
        contacts={workflow.linked_contacts}
        labelStyle={detailLabelStyle}
        valueStyle={detailValueStyle}
      />

      <div style={detailLabelStyle}>Workflow</div>
      <div style={detailValueStyle}>{workflow.name || 'Untitled Workflow'}</div>

      {workflow.description && (
        <>
          <div style={detailLabelStyle}>Description</div>
          <div style={detailValueStyle}>
            <Markup value={workflow.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        </>
      )}

      {workflow.category && (
        <>
          <div style={detailLabelStyle}>Category</div>
          <div style={detailValueStyle}>{workflow.category}</div>
        </>
      )}

      {dueFormatted && (
        <>
          <div style={detailLabelStyle}>Due Date</div>
          <div style={detailValueStyle}>{dueFormatted}</div>
        </>
      )}

      {currentStep && (
        <>
          <div style={detailLabelStyle}>Current Step</div>
          <div style={detailValueStyle}>{currentStep}</div>
        </>
      )}
    </div>
  )
}

WorkflowDetail.propTypes = {
  workflow: PropTypes.object.isRequired
}

function WorkflowRow ({ workflow, expanded, onToggle }) {
  const status = getWorkflowStatus(workflow)
  const dueFormatted = workflow.due_date
    ? dayjs(workflow.due_date).format('MMM D, YYYY')
    : null

  return (
    <div style={{ borderBottom: '1px solid var(--color-neutral-200)' }}>
      <div
        role='button'
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle() } }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 1.5rem',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)'
        }}
        >
          <Icon
            name={status === 'overdue' ? 'clock' : 'radioUnchecked'}
            customSize='18px'
          />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-color-subtle)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            <LinkedContactRow contacts={workflow.linked_contacts} />
          </div>
          <div style={{
            fontSize: 'var(--font-size-sm)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          >
            {workflow.name || 'Untitled Workflow'}
          </div>
          {dueFormatted && (
            <div style={{
              fontSize: 'var(--font-size-xs)',
              color: status === 'overdue' ? 'var(--color-decline-500)' : 'var(--text-color-subtle)',
              marginTop: '2px'
            }}
            >
              Due {dueFormatted}
            </div>
          )}
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          color: 'var(--text-color-subtle)',
          transition: 'transform 150ms',
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)'
        }}
        >
          <Icon name='chevronDown' customSize='16px' />
        </div>
      </div>
      <Collapse in={expanded} timeout={200}>
        <div style={{
          background: 'linear-gradient(0deg, var(--color-surface-200) 90%, var(--color-surface-300))',
          borderRadius: 'var(--border-radius-sm)',
          marginBottom: '4px'
        }}
        >
          <WorkflowDetail workflow={workflow} />
        </div>
      </Collapse>
    </div>
  )
}

WorkflowRow.propTypes = {
  workflow: PropTypes.object.isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func.isRequired
}

function RedtailWorkflowPanel ({ open, onClose, items, title }) {
  const [expandedId, setExpandedId] = useState(null)
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL)

  const sorted = useMemo(() => {
    if (!items?.length) return []
    return [...items].sort((a, b) => {
      const da = a.due_date ? dayjs(a.due_date).valueOf() : Infinity
      const db = b.due_date ? dayjs(b.due_date).valueOf() : Infinity
      return da - db
    })
  }, [items])

  const filtered = useMemo(() => {
    if (statusFilter === FILTER_ALL) return sorted
    return sorted.filter(w => getWorkflowStatus(w) === statusFilter)
  }, [sorted, statusFilter])

  const handleToggle = useCallback((id) => {
    setExpandedId(prev => prev === id ? null : id)
  }, [])

  return (
    <RightPanel
      open={open}
      toggleOpen={onClose}
      title={title || 'Workflows'}
      showCloseButton
    >
      <div style={{ paddingBottom: '1.5rem' }}>
        {sorted.length > 0 && (
          <div style={{ paddingTop: '4px', paddingBottom: '12px', display: 'flex', justifyContent: 'center', padding: '4px 1.5rem 12px' }}>
            <SydTabs.Group
              tabOptions={statusTabOptions}
              defaultValue={FILTER_ALL}
              onChange={setStatusFilter}
            >
              <SydTabs.MiniList />
            </SydTabs.Group>
          </div>
        )}

        {!filtered.length
          ? (
            <FlexRow
              className='justify-center items-center'
              style={{
                minHeight: '120px',
                padding: '0 1.5rem',
                color: 'var(--text-color-subtle)',
                fontSize: 'var(--font-size-sm)'
              }}
            >
              {sorted.length ? 'No matching workflows' : 'No current workflows'}
            </FlexRow>
          )
          : filtered.map(workflow => (
            <WorkflowRow
              key={workflow.id}
              workflow={workflow}
              expanded={expandedId === workflow.id}
              onToggle={() => handleToggle(workflow.id)}
            />
          ))}
      </div>
    </RightPanel>
  )
}

RedtailWorkflowPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  items: PropTypes.array,
  title: PropTypes.string
}

export default RedtailWorkflowPanel
